import { useQuery } from "@tanstack/react-query";
import { lazy, Suspense } from "react";
import { useParams } from "react-router";
import { ApiError, errorMessage, publicApi, unwrap } from "../api";
import { locationAgeText, mapsUrl, SOS_POLL_MS, sosHeadline } from "../contacts/contacts";
import { useNow } from "../useNow";
import { LegalLayout } from "./PrivacyPage";

const SosMap = lazy(() => import("../map/SosMap"));

const isGone = (error: unknown) => error instanceof ApiError && error.status === 404;

/**
 * Public: the `/sos/{token}` link an SOS emails to each confirmed emergency
 * contact. Shows the sender's location, refreshed every 15 s, until the alert
 * ends; the link stops working an hour later (at most 24 h after it started).
 */
export function SosLinkPage() {
  const { token = "" } = useParams();
  const nowMs = useNow(SOS_POLL_MS);
  const link = useQuery({
    queryKey: ["sos-link", token],
    queryFn: async () => unwrap(await publicApi.GET("/sos-links/{token}", { params: { path: { token } } })),
    // Poll while open; a 404 (expired, contact removed) ends it for good.
    refetchInterval: (query) =>
      query.state.data?.state === "open" && !isGone(query.state.error) ? SOS_POLL_MS : false,
    refetchIntervalInBackground: true,
    retry: (failures, error) => !isGone(error) && failures < 2,
  });
  // Never show what an earlier poll returned once the link is dead.
  const gone = isGone(link.error) || (link.data !== undefined && Date.parse(link.data.expires_at) <= nowMs);
  const data = gone ? undefined : link.data;
  const location = data?.location ?? null;
  return (
    <LegalLayout title={data ? sosHeadline(data) : "SOS"}>
      {link.isPending && <p className="muted">Loading…</p>}
      {gone && (
        <p className="muted">
          This link no longer works. Links work until an hour after the alert ends (at most 24 hours after it started),
          and stop when the sender removes you as a contact.
        </p>
      )}
      {link.error && !gone && (
        <p className="error" role="alert">
          {errorMessage(link.error)}
        </p>
      )}
      {data && (
        <>
          {data.state === "open" ? (
            <p className="error" role="status">
              <strong>
                ten20 does not contact emergency services. If you think {data.name} is in danger, call your local
                emergency number.
              </strong>
            </p>
          ) : (
            <p className="muted" role="status">
              {data.state === "resolved"
                ? "The alert has ended, so their location is no longer shown."
                : "The alert ended after 24 hours, so their location is no longer shown."}
            </p>
          )}
          {data.state === "open" &&
            (location ? (
              <>
                <p>
                  {locationAgeText(location.ts, nowMs)} · within {Math.round(location.accuracy_m)} m ·{" "}
                  <a href={mapsUrl(location.lat, location.lon)} target="_blank" rel="noreferrer noopener">
                    Open in maps
                  </a>
                </p>
                <Suspense fallback={<div className="live-map sos-map" />}>
                  <SosMap location={location} />
                </Suspense>
              </>
            ) : (
              <p className="muted">No location yet. This page updates by itself.</p>
            ))}
          <p className="hint">
            {data.state === "open" ? "This page updates every 15 seconds. " : ""}
            The link stops working at {new Date(data.expires_at).toLocaleString()}.
          </p>
        </>
      )}
    </LegalLayout>
  );
}
